import React, { useRef } from 'react';
import { UserHistoryTaskResp } from 'domain/payload/response/user/UserViewResp';

interface HistoryListViewProps {
  history: UserHistoryTaskResp[] | null;
  loading: boolean;
  contentHeight: string;
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  formatDate?: (dateString: string) => string;
}

const HistoryListView: React.FC<HistoryListViewProps> = ({
  history,
  loading,
  contentHeight,
  currentPage,
  pageSize,
  totalRecords,
  totalPages,
  onPageChange,
  onPageSizeChange,
  formatDate
}) => {
  const historyListRef = useRef<HTMLDivElement>(null);
  const hasHistory = history && history.length > 0;

  const defaultFormatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateFn = formatDate || defaultFormatDate;

  return (
    <>
      <div 
        ref={historyListRef}
        style={{ 
          height: contentHeight,
          overflowY: 'auto',
          minHeight: '450px'
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading history...</span>
            </div>
            <p className="mt-2">Loading history...</p>
          </div>
        ) : hasHistory ? (
          <div className="list-group list-group-flush">
            {(history || []).map((task, index) => {
              const isRejected = task.status?.key === 5;
              
              return (
                <div key={task.resultId} className="list-group-item border-0 px-3 py-3">
                  <div className="d-flex w-100 justify-content-between align-items-start mb-1">
                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-semibold">{task.taskName || 'Unnamed Task'}</h6>
                      
                      {isRejected && task.notes && (
                        <div className="mb-2">
                          <small className="text-danger">
                            {task.notes}
                          </small>
                        </div>
                      )}
                    </div>
                    <span className={`badge ${isRejected ? 'bg-danger' : task.status?.value === 'Done' ? 'bg-success' : 'bg-warning'}`}>
                      {task.status?.value || 'Unknown'}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                      <small className="text-muted me-3">
                        <i className="fa-solid fa-calendar me-1"></i>
                        {task.submitDate ? formatDateFn(task.submitDate) : 'N/A'}
                      </small>
                    </div>
                  </div>

                  <div>
                    <small className="text-muted">
                      <i className="fa-solid fa-location-dot me-1"></i>
                      {task.latitude?.toFixed(6) || '0'}, {task.longitude?.toFixed(6) || '0'}
                    </small>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center d-flex flex-column justify-content-center align-items-center" style={{ height: '100%' }}>
            <i className="fa-solid fa-history text-muted fa-3x mb-2"></i>
            <p className="text-muted">No task history</p>
          </div>
        )}
      </div>

      {hasHistory && (
        <div className="d-flex justify-content-end align-items-center p-3 border-top">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <label className="me-2 small">Rows:</label>
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                {[5, 10, 25, 50].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <span className="me-3 small">
              {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
            </span>

            <nav>
              <ul className="pagination mb-0 pagination-sm">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(1)} style={{ border: 0 }} disabled={currentPage === 1}>
                    <i className="fa fa-angle-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(currentPage - 1)} style={{ border: 0 }} disabled={currentPage === 1}>
                    <i className="fa fa-angle-left"></i>
                  </button>
                </li>

                <li className="page-item disabled">
                  <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                </li>

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(currentPage + 1)} style={{ border: 0 }} disabled={currentPage === totalPages}>
                    <i className="fa fa-angle-right"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(totalPages)} style={{ border: 0 }} disabled={currentPage === totalPages}>
                    <i className="fa fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryListView;